import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ZapIcon, BarChart3Icon, UsersIcon, MailIcon, PhoneIcon, CalendarIcon, BrainIcon, ShieldCheckIcon } from 'lucide-react';
const benefits = [{
  icon: ZapIcon,
  title: 'Automated Workflows',
  description: 'Eliminate repetitive tasks. Leads are automatically assigned, follow-ups scheduled, and reminders sent.',
  metric: '89% time saved'
}, {
  icon: BarChart3Icon,
  title: 'Real-time Pipeline Overview',
  description: 'See at a glance where every deal stands. Accurate revenue forecasts based on real data, not gut feeling.',
  metric: '95% forecast accuracy'
}, {
  icon: UsersIcon,
  title: 'Team Transparency',
  description: 'Every activity is logged automatically. You know exactly who discussed what with which customer and when.',
  metric: '100% activity tracking'
}, {
  icon: MailIcon,
  title: 'Email Sequences',
  description: 'Create personalized email campaigns that are automatically sent at the right time. Never forget a follow-up again.',
  metric: '3x higher response rate'
}, {
  icon: PhoneIcon,
  title: 'Integrated Telephony',
  description: 'Call directly from Close. Conversations are automatically recorded and transcribed. Full integration with your phone system.',
  metric: '40% more calls/day'
}, {
  icon: CalendarIcon,
  title: 'Smart Scheduling',
  description: 'Automatic appointment scheduling with calendar sync. Customers book themselves – without email ping-pong.',
  metric: '67% fewer no-shows'
}, {
  icon: BrainIcon,
  title: 'AI-powered Insights',
  description: 'Close analyzes your data and provides recommendations. Which deals need attention? Where are the opportunities?',
  metric: '23% more conversions'
}, {
  icon: ShieldCheckIcon,
  title: 'GDPR Compliant',
  description: 'All data is securely stored in the EU. Full GDPR compliance with audit trail and deletion capabilities.',
  metric: '100% compliance'
}];
export function BenefitsGrid() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 lg:py-32 bg-[#0a0a0a]" ref={ref} data-id="element-11" data-mp="2x1zsyohwfiku">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-12" data-mp="2wzdleov0d32m">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-13" data-mp="2t0mmcc6zfbcv">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#0ea5e9]/10 text-[#0ea5e9] text-sm font-medium mb-6" data-id="element-14" data-mp="h999excli9rx">
            Your Benefits
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-15" data-mp="kshri5cwm2si">
            8 reasons why Close CRM transforms your sales
          </h2>
          <p className="mt-6 text-lg text-gray-400" data-id="element-16" data-mp="orqkv9417ynj">
            Every benefit is measurable. We show you exactly what impact you can
            expect.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-17" data-mp="yiuzg7psphhu">
          {benefits.map((benefit, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="group p-6 rounded-2xl bg-[#1a1a1a] border border-white/5 hover:border-[#0ea5e9]/30 transition-colors duration-300" data-id="element-18" data-mp="1vgnonv368q5" data-mpi={index}>
              <div className="w-12 h-12 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center mb-4 group-hover:bg-[#0ea5e9]/20 transition-colors" data-id="element-19" data-mp="3073dxvt2m88k">
                <benefit.icon className="w-6 h-6 text-[#0ea5e9]" data-id="element-20" data-mp="396zu191e9ys0" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" data-id="element-21" data-mp="346kjsqcet7e9">
                {benefit.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4" data-id="element-22" data-mp="3j2th3mcqpao1">
                {benefit.description}
              </p>
              <div className="pt-4 border-t border-white/5" data-id="element-23" data-mp="3e2xcbkw7xf7f">
                <span className="text-[#0ea5e9] font-semibold text-sm" data-id="element-24" data-mp="3rvdbubpwuv3l">
                  {benefit.metric}
                </span>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}